import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { TControlSize } from '../../tokens/types';
import { vars } from '../../theme';

const containerStyles = {
    sm: css`
        border-radius: ${vars.radius.sm};
        max-height: 36px;
    `,
    md: css`
        border-radius: ${vars.radius.md};
        max-height: 42px;
    `,
    lg: css`
        border-radius: ${vars.radius.md};
        max-height: 48px;
    `
};

interface ContainerProps {
    $stretchToFill: boolean;
    $size: TControlSize;
}

export const Container = styled.div<ContainerProps>`
    display: flex;
    gap: 2px;
    padding: 2px;
    background: ${vars.colors.surface.itemInset};
    border: 1px solid ${vars.colors.stroke.secondary};
    box-sizing: border-box;
    position: relative;
    width: ${props => (props.$stretchToFill ? '100%' : 'fit-content')};
    ${props => containerStyles[props.$size]}
`;

interface OptionWrapperProps {
    $size: TControlSize;
    $stretchToFill: boolean;
}

const sizeStyles = {
    sm: css`
        border-radius: calc(${vars.radius.sm} - 2px);
    `,
    md: css`
        border-radius: calc(${vars.radius.md} - 2px);
    `,
    lg: css`
        border-radius: calc(${vars.radius.md} - 2px);
    `
};

const sizePadding = {
    sm: css`
        padding: 6px 7px;
    `,
    md: css`
        padding: 8px 10px;
    `,
    lg: css`
        padding: 8px 13px;
    `
};

const sizeMinDimensions = {
    sm: css`
        min-height: 30px;
        min-width: 30px;
    `,
    md: css`
        min-height: 36px;
        min-width: 36px;
    `,
    lg: css`
        min-height: 42px;
        min-width: 42px;
    `
};

const sizeGap = {
    sm: css`
        gap: 6px;
    `,
    md: css`
        gap: 8px;
    `,
    lg: css`
        gap: 8px;
    `
};

export const OptionWrapper = styled.div<OptionWrapperProps>`
    position: relative;
    display: flex;
    box-sizing: border-box;

    ${props => sizeStyles[props.$size]}
    ${props => sizeMinDimensions[props.$size]}
    
    ${props =>
        props.$stretchToFill &&
        css`
            flex: 1;
        `}

    &:has(button:focus-visible) {
        z-index: 10;

        &::before {
            content: '';
            position: absolute;
            inset: 0;
            border-radius: inherit;
            pointer-events: none;
            z-index: 1;
            box-shadow: 0px 0px 0px 1px ${vars.colors.text.invertedPrimary},
                0px 0px 0px 3px ${vars.colors.stroke.keyboardFocus};
        }
    }
`;

const buttonResetStyles = css`
    all: unset;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    border-radius: inherit;
    box-sizing: border-box;
`;

export const SelectedIndicator = styled(motion.div)`
    position: absolute;
    inset: 0;
    background: ${vars.colors.surface.item};
    border: 1px solid ${vars.colors.stroke.secondary};
    border-radius: inherit;
    z-index: 0;
    box-sizing: border-box;
`;

export const ItemContainer = styled.button<{
    $size: TControlSize;
    $disabled: boolean;
}>`
    ${buttonResetStyles}
    cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};

    ${props => sizePadding[props.$size]}
    ${props => sizeGap[props.$size]}

    &:hover,
    &:focus,
    &:active {
        ${buttonResetStyles}
        cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
        ${props => sizePadding[props.$size]}
        ${props => sizeGap[props.$size]}
    }

    ${props =>
        !props.$disabled &&
        css`
            &[data-state='off']:hover .hover-background {
                opacity: 1;
            }
        `}

    &:focus-visible {
        outline: none;

        ${SelectedIndicator} {
            z-index: -1;
        }
    }
`;

export const OptionContent = styled.div<{
    $size: TControlSize;
}>`
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    position: relative;
    white-space: nowrap;
    text-overflow: ellipsis;

    ${props => sizeGap[props.$size]}
`;

export const IconWrapper = styled.span<{
    $isSelected: boolean;
    $isDisabled: boolean;
}>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    flex-shrink: 0;

    i {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        line-height: 0;
    }

    svg {
        width: 16px;
        height: 16px;
    }

    /* Target path and use so icons with either structure get theme color (e.g. List16 uses <use>). */
    svg path,
    svg use {
        fill: ${props =>
            props.$isDisabled
                ? vars.colors.text.disabled
                : props.$isSelected
                ? vars.colors.text.secondary
                : vars.colors.text.tertiary};
    }
`;

export const HoverBackground = styled.div`
    position: absolute;
    inset: 0;
    background: ${vars.colors.surface.itemInsetHover};
    border-radius: inherit;
    opacity: 0;
    transition: opacity 0.2s ease;
    z-index: 0;
    box-sizing: border-box;
`;
